import React from 'react';
import { toast } from 'sonner';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { BookOpen, Clock, Briefcase, ShieldCheck, Lightbulb, Download } from 'lucide-react';
import { COMPONENT_INFO, ROLE_LABELS } from '../../pages/estimator/constants';
import { getGroupLabel } from '../../lib/groupLabels';
import { exportComponentFactsheet } from '../../lib/factsheetPdf';

/**
 * Read-only knowledgebase drawer for a single component.
 * Renders description, rationale, base hours, role & group.
 */
export function ComponentDocSheet({ open, onOpenChange, component }) {
  if (!component) return null;
  const info = COMPONENT_INFO[component.name] || {};
  const rationale = component.required
    ? (component.required_reason || info.hours_rationale || 'This is a mandatory component for every Seertech LMS implementation \u2014 it cannot be removed from the scope.')
    : component.recommended
      ? (component.recommended_reason || info.hours_rationale || 'Recommended default for most implementations. Can be de-scoped.')
      : info.hours_rationale;
  const description = info.description
    || `${component.name} is a ${getGroupLabel(component.group, 'default')} component${component.role ? ` delivered by ${ROLE_LABELS[component.role] || component.role}` : ''}.`;
  const hours = component.base_hours || {};

  const exportPdf = () => {
    try {
      const filename = exportComponentFactsheet(component);
      toast.success(`Exported ${filename}`);
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-md overflow-y-auto" data-testid="component-doc-sheet">
        <SheetHeader className="space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <SheetTitle className="text-lg text-[#0F2040]">{component.name}</SheetTitle>
            {component.required && <Badge className="bg-[#0F2040] text-white text-[10px]">Required</Badge>}
            {!component.required && component.recommended && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Recommended</Badge>}
          </div>
          <SheetDescription className="text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {getGroupLabel(component.group, 'default')}</span>
            {component.role && (
              <span className="ml-3 inline-flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-slate-300" /> {ROLE_LABELS[component.role] || component.role}
              </span>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-5">
          {/* Description */}
          <section data-testid="doc-description">
            <div className="flex items-center gap-2 mb-1.5">
              <BookOpen className="w-3.5 h-3.5 text-slate-400" />
              <h4 className="text-xs font-semibold text-[#0F2040] uppercase tracking-wide">About</h4>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">{description}</p>
          </section>

          {/* Rationale */}
          {rationale && (
            <section data-testid="doc-rationale">
              <div className="flex items-center gap-2 mb-1.5">
                {component.required
                  ? <ShieldCheck className="w-3.5 h-3.5 text-[#0F2040]" />
                  : <Lightbulb className="w-3.5 h-3.5 text-amber-500" />}
                <h4 className="text-xs font-semibold text-[#0F2040] uppercase tracking-wide">
                  {component.required ? 'Why it is required' : component.recommended ? 'Why it is recommended' : 'Rationale'}
                </h4>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{rationale}</p>
            </section>
          )}

          {/* Base hours */}
          {(hours.simple || hours.standard || hours.complex) && (
            <section data-testid="doc-base-hours">
              <div className="flex items-center gap-2 mb-1.5">
                <Clock className="w-3.5 h-3.5 text-slate-400" />
                <h4 className="text-xs font-semibold text-[#0F2040] uppercase tracking-wide">Base effort by complexity</h4>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {['simple', 'standard', 'complex'].map((level) => (
                  <Card key={level}>
                    <CardContent className="p-2.5 text-center">
                      <p className="text-[9px] uppercase tracking-wide text-slate-400 mb-0.5">{level}</p>
                      <p className="text-base font-bold text-[#0F2040]">{hours[level] ?? 0}</p>
                      <p className="text-[9px] text-slate-400">hrs</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-2 italic">
                Actual effort is modified by project factors, implementation type, and data-integrity uplift.
              </p>
            </section>
          )}

          {/* Export */}
          <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
            <p className="text-[10px] text-slate-400 flex-1">
              Share this component's scope definition with clients or internal teams.
            </p>
            <Button size="sm" onClick={exportPdf}
              className="bg-[#0F2040] hover:bg-[#1A3055] text-white h-8 text-xs shrink-0"
              data-testid="doc-export-pdf-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export Factsheet
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
